<template>
  <Modal
    class="date-time-modal modal-dark"
    :onClose="hideDateTimeModal">
    <div slot="header">{{$t('time.title')}}</div>
    <div
      v-if="minMonth"
      class="content-container">
      <div
        :class="{'alert-warning': dateTimeError}"
        class="alert alert-info">
        <p v-if="!dateTimeError" class="alert-text">
          <i class="far fa-info-circle"></i> {{$t('dateTime.minDateNotice')}} {{minDateTimeText}}
        </p>
        <p v-if="dateTimeError" class="alert-text">
          <i class="far fa-calendar-times"></i> {{$t('validation:dateTimeOutOfRange')}}
        </p>
      </div>
      <div class="date-container">
        <md-field class="input-group">
          <label>{{$t('common:day')}}</label>
          <md-input v-model="date" type="number" min="1" max="31" step="1"></md-input>
        </md-field>
        <md-field class="input-group input-month">
          <label for="month">{{$t('common:month')}}</label>
          <md-select
            id="month"
            name="month"
            v-model="month">
            <md-option
              v-for="(monthIterator, index) in availableMonths"
              :value="monthIterator"
              :key="index">
              {{ monthText[monthIterator - 1] }}
            </md-option>
          </md-select>
        </md-field>
        <md-field class="input-group">
          <label>{{$t('common:year')}}</label>
          <md-input v-model="year" type="number" :min="minYear" :max="maxYear" step="1"></md-input>
        </md-field>
      </div>
      <h6>{{$t('common:time')}}</h6>
      <div class="time-container">
        <md-field class="input-group">
          <label>{{$t('common:hour')}}</label>
          <md-input type="number" min="0" max="23" step="1" v-model="hour"></md-input>
        </md-field>
        <md-field class="input-group">
          <label>{{$t('common:minute')}}</label>
          <md-input type="number" min="0" max="59" step="1" v-model="minute"></md-input>
        </md-field>
        <md-field class="input-group">
          <label>{{$t('common:second')}}</label>
          <md-input type="number" min="0" max="59" step="1" v-model="second"></md-input>
        </md-field>
      </div>
      <div class="button-container">
        <button
          type="button"
          class="btn btn-outline-light"
          @click.stop="setOldestDateTime">
          {{$t('time.goToMinDateTime')}}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click.stop="onSubmit">
          {{$t('common:confirm')}}
        </button>
      </div>
    </div>
    <div
      v-if="!minMonth"
      class="content-container">
      <div class="alert alert-warning">
        <p class="alert-text">
          <i class="fas fa-video-slash"></i> {{$t('validation:cannotFindSource')}}
        </p>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import Modal from '@/components/elements/Modal.vue';

export default {
  data() {
    return {
      date: -1,
      month: -1,
      year: -1,
      hour: -1,
      minute: -1,
      second: -1,
      now: -1,
      availableMonths: [],
    };
  },
  components: {
    Modal,
  },
  computed: {
    ...mapState({
      minDateTime: state => state.timeline.minDateTime,
      maxDateTime: state => state.timeline.maxDateTime,
      isDateTimeModalShowing: state => state.timeline.isDateTimeModalShowing,
    }),
    ...mapGetters([
      'selectedDateTime',
    ]),
    minDate() {
      return this.minDateTime.getDate();
    },
    minMonth() {
      if (this.minDateTime) {
        return this.minDateTime.getMonth() + 1;
      }
      return 1;
    },
    minYear() {
      return this.minDateTime.getFullYear();
    },
    minHour() {
      return this.minDateTime.getHours();
    },
    minMinute() {
      return this.minDateTime.getMinutes();
    },
    minSecond() {
      return this.minDateTime.getSeconds() + 1;
    },
    maxDate() {
      return this.maxDateTime.getDate();
    },
    maxMonth() {
      return this.maxDateTime.getMonth() + 1;
    },
    maxYear() {
      return this.maxDateTime.getFullYear();
    },
    maxHour() {
      return this.maxDateTime.getHours();
    },
    maxMinute() {
      return this.maxDateTime.getMinutes();
    },
    maxSecond() {
      return this.maxDateTime.getSeconds();
    },
    monthText() {
      return [
        this.$t('common:january'),
        this.$t('common:february'),
        this.$t('common:march'),
        this.$t('common:april'),
        this.$t('common:may'),
        this.$t('common:june'),
        this.$t('common:july'),
        this.$t('common:august'),
        this.$t('common:september'),
        this.$t('common:october'),
        this.$t('common:november'),
        this.$t('common:december'),
      ];
    },
    dateTimeError() {
      return false;
    },
    minDateTimeText() {
      let output = `${this.minDate} ${this.monthText[this.minMonth - 1]} ${this.minYear}`;
      output += ` ${this.minHour}:${this.minMinute}:${this.minSecond}`;
      return output;
    },
  },
  watch: {
    month(newValue) {
      if (!this.isDateTimeModalShowing) {
        return;
      }
      if (newValue > this.maxMonth) {
        this.month = this.maxMonth;
      }
    },
    year(newValue) {
      const newYear = parseInt(newValue, 10);
      if (!this.isDateTimeModalShowing) {
        return;
      }
      const thisYear = this.now.getFullYear();
      if (newYear > thisYear) {
        this.year = thisYear;
      }
    },
  },
  created() {
    this.now = new Date();
    this.getAvailableMonths();
    if (this.selectedDateTime) {
      const date = new Date(this.selectedDateTime);
      this.date = date.getDate();
      this.month = date.getMonth() + 1;
      this.year = date.getFullYear();
      this.hour = date.getHours();
      this.minute = date.getMinutes();
      this.second = date.getSeconds();
    } else {
      this.date = this.maxDate;
      this.month = this.maxMonth;
      this.year = this.maxYear;
      this.hour = this.maxHour;
      this.minute = this.maxMinute;
      this.second = this.maxSecond;
    }
  },
  methods: {
    ...mapActions([
      'hideDateTimeModal',
      'setSelectedDateTime',
    ]),
    getAvailableMonths() {
      for (let i = this.minMonth; i <= this.maxMonth; i += 1) {
        this.availableMonths.push(i);
      }
    },
    getSelectedDateTime() {
      const month = this.month - 1;
      return new Date(this.year, month, this.date, this.hour, this.minute, this.second);
    },
    setOldestDateTime() {
      this.date = this.minDate;
      this.month = this.minMonth;
      this.year = this.minYear;
      this.hour = this.minHour;
      this.minute = this.minMinute;
      this.second = this.minSecond;
    },
    onSubmit() {
      let newDate = this.getSelectedDateTime();
      newDate = newDate.toISOString();
      this.setSelectedDateTime(newDate);
      this.hideDateTimeModal();
    },
  },
};
</script>

<style lang="scss" scoped>
.content-container {
  max-width: 90%;
  margin: 20px auto 0;
  .alert-text {
    font-size: 14px;
  }
  .date-container {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;
    .input-group {
      display: flex;
      flex-flow: column;
      flex: 0 0 15%;
      margin-right: 2%;
      input {
        width: 100%;
      }
      &.input-month {
        flex: 0 0 20%;
      }
      .month-dropdown {
        width: 110px;
        padding: 2px;
        border-radius: 0.15rem;
      }
    }
  }
  .time-container {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;
    .input-group {
      display: flex;
      flex-flow: column;
      flex: 0 0 15%;
      margin-right: 2%;
      input {
        width: 100%;
      }
    }
  }
  .button-container {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
  }
}
</style>
